import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, Modal } from '@bringg/react-components';
import { Notification } from '@bringg/react-components';
import { BringgIcon, BringgFontIcons } from '@bringg/bringg-icons';
import papaparse from 'papaparse';
import { ResourceUploadResult } from '@bringg/dashboard-sdk/dist/ResourceUpload/ResourceResults';
import ResourceUploadType from '@bringg/dashboard-sdk/dist/ResourceUpload/ResourceUploadType';
import { Failure } from '@bringg-frontend/batch-action-status';
import { downloadFile } from '@bringg-frontend/utils';
import { CsvImportMappingTable } from '@bringg-frontend/csv-import-mapping-table';
import { ApplicationUuid } from '@bringg/types';

import { BringgDragger, ResourceAsyncOperationStatus, resourceUploader, useStores } from 'bringg-web';
import useDateTimeFormat from 'bringg-web/hooks/use-date-time-format';
import { useCSVImportTranslation } from 'bringg-web/features/planned-delivery-windows/exclusion-windows/exclusion-windows-csv-import/utils/translations';
import {
	getMissingMandatoryFields,
	exclusionWindowsColumns,
	exclusionWindowsColumnsByDataIndex,
	exclusionWindowsColumnsTreeData,
	MANDATORY_FIELDS_KEY,
	OPTIONAL_FIELDS_KEY,
	TASKS_LIMIT_COLUMN
} from '../exclusion-windows-csv-import/utils/columns';
import { arrayToCSV, csvToExclusionWindows, exclusionWindowsToJSONL } from './utils/exclusion-window-csv-to-jsonl';

import styles from './exclusion-windows-csv-import.module.scss';

const LOCAL_KEY = 'exclusion-windows-mapping-table';
const PREVIEW_ROWS = 5;
const MODAL_WIDTH = 960;
const DRAGGER_HEIGHT = 388;

const DONE_EVENT = 'exclusion_windows_import_done';
const DEFAULT_FILE_NAME = 'exclusion_windows.csv';

const ExclusionWindowsCsvImport: FC<{ open: boolean; onClose: () => void; onComplete: () => void }> = ({
	open,
	onClose,
	onComplete
}) => {
	const translations = useCSVImportTranslation();
	const dateTimeFormat = useDateTimeFormat();
	const { applicationStore } = useStores();
	const hasTaskLimitInstalled = Boolean(
		applicationStore.getApplication(ApplicationUuid.ExclusionWindowTasksLimitApp)
	);

	const [file, setFile] = useState<File>(null);
	const [originalHeaders, setOriginalHeaders] = useState<string[]>([]);
	const [headers, setHeaders] = useState<string[]>([]);
	const [parsedFile, setParsedFile] = useState<string[][] | null>(null);
	const [keepMapping, setKeepMapping] = useState(true);
	const { usersStore } = useStores();
	const [uploadStatus, setUploadStatus] = useState<ResourceUploadResult>(null);
	const [uploadIsLoading, setUploadIsLoading] = useState(false);

	const closeAndReset = useCallback(() => {
		setFile(null);
		setOriginalHeaders([]);
		setHeaders([]);
		setParsedFile(null);
		onClose();
	}, [onClose]);

	const onParseComplete = useCallback(
		({ data }) => {
			if (!data?.length) {
				closeAndReset();
				return Notification.error(translations.failedToParseFile);
			}

			const localHeaders = JSON.parse(localStorage.getItem(LOCAL_KEY) || 'null') || {};
			const parsedHeaders = data[0].map(column => {
				if (localHeaders[column] === null) {
					return null;
				}

				return (
					localHeaders[column] ||
					exclusionWindowsColumns.find(({ dataIndex }) => dataIndex === column)?.dataIndex ||
					null
				);
			});

			setHeaders(parsedHeaders);
			setParsedFile(data);
			setOriginalHeaders(data[0]);
		},
		[closeAndReset, translations.failedToParseFile]
	);

	useEffect(() => {
		if (!file) {
			return;
		}

		papaparse.parse(file, {
			worker: true,
			skipEmptyLines: 'greedy',
			dynamicTyping: true,
			error: ex => {
				console.error(ex);
				Notification.error(translations.failedToParseFile);
				closeAndReset();
			},
			complete: onParseComplete
		});
	}, [file, onParseComplete, closeAndReset]);

	const missingMandatoryFields = useMemo(() => getMissingMandatoryFields(headers), [headers]);

	const allMatched = useMemo(
		() => headers.filter(el => el).length === originalHeaders.length,
		[headers, originalHeaders]
	);
	const showWarning = useMemo(
		() => file && Boolean(missingMandatoryFields.length) && allMatched,
		[missingMandatoryFields, allMatched, file]
	);

	const startUpload = useCallback(
		async exclusionWindows => {
			setUploadIsLoading(true);
			try {
				const prev = JSON.parse(localStorage.getItem(LOCAL_KEY) || 'null') || {};
				const next = {
					...prev,
					...originalHeaders.reduce((acc, original, index) => ({ ...acc, [original]: headers[index] }), {})
				};

				if (keepMapping) {
					localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
				}

				setUploadStatus(
					await resourceUploader.prepareAndProcess(
						exclusionWindowsToJSONL(exclusionWindows, file.name, dateTimeFormat),
						'exclusion_windows' as ResourceUploadType,
						DONE_EVENT,
						{}
					)
				);

				// close modal, don't reset status
				onClose();
			} catch (ex) {
				closeAndReset();
				console.error(ex);
				Notification.error(translations.uploadFailed);
			} finally {
				setUploadIsLoading(false);
			}
		},
		[translations, keepMapping, file, headers]
	);

	const footer = useMemo(() => {
		if (!file) {
			return null;
		}

		const uploadFile = () => {
			const exclusionWindows = csvToExclusionWindows(parsedFile, headers);

			void startUpload(exclusionWindows);
		};

		return (
			<div className={styles.row}>
				<Button
					loading={uploadIsLoading}
					type="primary"
					disabled={Boolean(missingMandatoryFields.length)}
					onClick={uploadFile}
				>
					{translations.approveAndGenerate}
				</Button>
			</div>
		);
	}, [
		file,
		translations,
		headers,
		missingMandatoryFields,
		parsedFile,
		usersStore.currentUser,
		startUpload,
		uploadIsLoading
	]);

	const getErrorsFile = useCallback(
		// show original headers and then failures
		(failures: Failure[]) => [parsedFile[0], ...failures.map(failure => parsedFile[Number(failure.id)])],
		[parsedFile]
	);

	const downloadErrors = useCallback(
		(failures: Failure[]) => {
			const fileName = file ? file.name : DEFAULT_FILE_NAME;

			downloadFile(arrayToCSV(getErrorsFile(failures), `errors_${fileName}`));
		},
		[file, getErrorsFile]
	);

	const renderFooter = useCallback(
		(failures: Failure[]) => {
			const hasFailures = Boolean(failures?.length);
			const closeFailures = () => {
				setUploadStatus(null);
				closeAndReset();
				onComplete();
			};

			return (
				<div className={styles.footer}>
					{!hasFailures ? (
						<Button type="primary" onClick={closeFailures}>
							{translations.finish}
						</Button>
					) : null}
					{hasFailures ? (
						<div>
							<Button onClick={closeFailures}>{translations.close}</Button>
							<Button
								className={styles.downloadAndClose}
								type="primary"
								onClick={() => {
									downloadErrors(failures);
									closeFailures();
								}}
							>
								{translations.downloadAndClose}
							</Button>
						</div>
					) : null}
				</div>
			);
		},
		[translations, closeAndReset, downloadErrors]
	);

	const initiallyExpanded = useMemo(() => {
		return missingMandatoryFields.length ? [MANDATORY_FIELDS_KEY] : [];
	}, [missingMandatoryFields]);

	const previewFirstRows = useMemo(() => {
		return parsedFile && parsedFile.slice(0, PREVIEW_ROWS);
	}, [parsedFile]);

	const treeDataWithTaskLimit = useMemo(() => {
		if (!hasTaskLimitInstalled) {
			return exclusionWindowsColumnsTreeData;
		}

		return exclusionWindowsColumnsTreeData.map(data => {
			if (data.key === OPTIONAL_FIELDS_KEY) {
				return {
					...data,
					children: [
						...data.children,
						{
							key: TASKS_LIMIT_COLUMN.dataIndex,
							translationTitleKey: TASKS_LIMIT_COLUMN.translationTitleKey
						}
					]
				};
			}

			return data;
		});
	}, [hasTaskLimitInstalled]);

	const columnsDataIndexWithTaskLimit = useMemo(() => {
		if (!hasTaskLimitInstalled) {
			return exclusionWindowsColumnsByDataIndex;
		}

		return {
			...exclusionWindowsColumnsByDataIndex,
			[TASKS_LIMIT_COLUMN.dataIndex]: TASKS_LIMIT_COLUMN
		};
	}, [hasTaskLimitInstalled]);

	return (
		<>
			<Modal
				destroyOnClose
				title={
					<div className={styles.modalTitle}>
						<BringgIcon className={styles.modalTitleIcon} iconName={BringgFontIcons.CsvFile} />
						{translations.uploadTitle}
					</div>
				}
				maskClosable={false}
				footer={footer}
				width={MODAL_WIDTH}
				open={open}
				onCancel={closeAndReset}
			>
				<div className={styles.modal}>
					{!file ? (
						<div>
							<div className={styles.secondaryText}>{translations.uploadDescription}</div>
							<BringgDragger
								className={styles.dragger}
								accept=".csv"
								height={DRAGGER_HEIGHT}
								onFileUploaded={setFile}
							/>
						</div>
					) : null}

					{parsedFile ? (
						<div>
							{showWarning ? (
								<div className={styles.warning}>
									<BringgIcon className={styles.warningIcon} iconName={BringgFontIcons.Warning} />
									<div className={styles.warningText}>
										<h5 className={styles.warningHeader}>{translations.mandatory}</h5>
										<span>{translations.mandatoryDescription}</span>
									</div>
								</div>
							) : null}

							<div className={styles.matched}>
								<span className={styles.mappingDescription}>{translations.mappingDescription}</span>
								<span>
									<span>
										{headers.filter(el => el).length} / {originalHeaders.length}{' '}
										{translations.matched}
									</span>
									{allMatched ? (
										<BringgIcon
											className={styles.matchedIcon}
											iconName={BringgFontIcons.Selected}
										/>
									) : null}
								</span>
							</div>

							<CsvImportMappingTable
								parsedFile={previewFirstRows}
								headers={headers}
								onChange={setHeaders}
								initiallyExpanded={initiallyExpanded}
								translations={translations}
								treeData={treeDataWithTaskLimit}
								columnsByDataIndex={columnsDataIndexWithTaskLimit}
							/>
							<div className={styles.keepMapping}>
								<Checkbox checked={keepMapping} onChange={e => setKeepMapping(e.target.checked)} />{' '}
								{translations.keepMapping}
							</div>
						</div>
					) : null}
				</div>
			</Modal>

			{uploadStatus ? (
				<ResourceAsyncOperationStatus
					className={styles.resourceStatus}
					title={
						<h4 className={styles.modalTitle}>
							<BringgIcon className={styles.modalTitleIcon} iconName={BringgFontIcons.CsvFile} />
							{translations.uploadTitle}
						</h4>
					}
					translations={translations}
					resourceId={uploadStatus.resource_id}
					width={MODAL_WIDTH}
					onClose={() => {
						setUploadStatus(null);
						closeAndReset();
						onComplete();
					}}
					renderFailures={failures => (
						<CsvImportMappingTable
							failures={failures}
							readonly
							parsedFile={getErrorsFile(failures)}
							headers={headers}
							translations={translations}
							treeData={treeDataWithTaskLimit}
							columnsByDataIndex={columnsDataIndexWithTaskLimit}
						/>
					)}
					renderFooter={renderFooter}
				/>
			) : null}
		</>
	);
};

export default ExclusionWindowsCsvImport;
